/**
 * 지문 등록 — 본인 확인 후 등록 (2026-10-15)
 *
 * 웹은 지문 자체를 읽지 못한다. 대신 «이 휴대폰이 만든 열쇠» 를 계정에 묶는다.
 * 그래서 한 휴대폰에는 한 계정만 등록되게 서버가 막는다(대리가입 선점 차단).
 */
import { useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { Fingerprint, AlertTriangle, CheckCircle2, X } from 'lucide-react';

/** 기기 지문 — 가입 때 쓰는 것과 같은 방식 */
function deviceFp(): string {
  try {
    const s = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let cv = '';
    try {
      const c = document.createElement('canvas');
      const x = c.getContext('2d');
      if (x) { x.textBaseline = 'top'; x.font = '14px Arial'; x.fillText('handy-fp-🔒', 2, 2); cv = c.toDataURL(); }
    } catch { /* 캔버스가 막힌 브라우저 */ }
    const raw = [navigator.userAgent, navigator.language, s, tz, cv].join('|');
    let h = 0;
    for (let i = 0; i < raw.length; i++) { h = ((h << 5) - h + raw.charCodeAt(i)) | 0; }
    return 'fp_' + Math.abs(h).toString(36) + '_' + raw.length;
  } catch { return ''; }
}

const n = (v: string) => v.replace(/[^0-9]/g, '');

export function PasskeySetup({ open, onClose, authHeaders, onDone }: {
  open: boolean; onClose: () => void;
  authHeaders: () => Record<string, string>; onDone?: () => void;
}) {
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const支 = null; // (미사용)

  const submit = async () => {
    setMsg(null);
    if (!nickname.trim()) return setMsg({ ok: false, text: '아이디를 입력해주세요' });
    if (n(phone).length < 10) return setMsg({ ok: false, text: '휴대폰번호를 정확히 입력해주세요' });
    if (!pin) return setMsg({ ok: false, text: '비밀번호를 입력해주세요' });
    if (pin !== pin2) return setMsg({ ok: false, text: '두 번 입력한 비밀번호가 서로 다릅니다' });

    setBusy(true);
    try {
      // ① 본인 확인 — 아이디·휴대폰·비밀번호가 모두 맞아야 통과
      const v = await fetch('/api/passkey/verify-owner', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ nickname: nickname.trim(), phone: n(phone), pin }),
      }).then(r => r.json());
      if (!v.success) throw new Error(v.error || '입력하신 정보가 맞지 않습니다');

      // ② 등록 옵션 — 이 기기에 다른 계정이 이미 있으면 여기서 막힌다
      const opt = await fetch('/api/passkey/register/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ deviceFingerprint: deviceFp() }),
      }).then(r => r.json());
      if (!opt.success) throw new Error(opt.error || '등록을 시작하지 못했습니다');

      // ③ 지문·얼굴 인증
      const cred = await startRegistration(opt.options);

      // ④ 서버 확인
      const fin = await fetch('/api/passkey/register/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ credential: cred, deviceFingerprint: deviceFp() }),
      }).then(r => r.json());
      if (!fin.success) throw new Error(fin.error || '등록 확인에 실패했습니다');

      setDone(true);
      setMsg({ ok: true, text: '지문이 등록되었습니다' });
      onDone?.();
    } catch (e: any) {
      const m = String(e?.message || e);
      setMsg({ ok: false, text:
        /NotAllowed|aborted|cancel/i.test(m) ? '지문 인증이 취소되었습니다' :
        /NotSupported|not supported/i.test(m)
          ? '이 브라우저에서는 지문 등록이 안 됩니다. 크롬 또는 사파리로 열어주세요'
          : m });
    } finally { setBusy(false); }
  };

  const input: React.CSSProperties = {
    width: '100%', padding: '13px 14px', borderRadius: 12, boxSizing: 'border-box',
    border: '1px solid #E5E7EB', fontSize: 16, background: '#F9FAFB', color: '#111827',
    outline: 'none', marginTop: 6,
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 320, background: 'rgba(0,0,0,.6)', padding: '16px 20px 90px' }}>
      <div className="w-full flex flex-col"
        style={{ background: '#fff', color: '#111827', maxWidth: 420,
                 maxHeight: '85vh', borderRadius: 20, overflow: 'hidden' }}>
        <div className="flex items-center justify-between"
          style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Fingerprint style={{ width: 20, height: 20, color: '#FF6B00' }} /> 지문 등록
          </h3>
          <button onClick={onClose} aria-label="닫기" className="flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, background: '#111827', flexShrink: 0 }}>
            <X className="h-5 w-5" style={{ color: '#fff' }} />
          </button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <CheckCircle2 style={{ width: 54, height: 54, color: '#16A34A', margin: '0 auto 14px' }} />
              <p style={{ fontSize: 18, fontWeight: 800 }}>지문이 등록되었습니다</p>
              <p style={{ fontSize: 14, color: '#6B7280', marginTop: 8, lineHeight: 1.6 }}>
                다음부터는 로그인할 때 지문으로 본인 확인을 합니다.<br />
                이 휴대폰에는 다른 계정을 등록할 수 없습니다.
              </p>
            </div>
          ) : (
            <>
              {/* 경고 — 가장 먼저 보이게 */}
              <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 14,
                            background: '#FEF2F2', border: '1px solid #FECACA', marginBottom: 16 }}>
                <AlertTriangle style={{ width: 20, height: 20, color: '#DC2626', flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 14, lineHeight: 1.65, color: '#991B1B' }}>
                  <b>반드시 본인 계정을 등록하세요.</b><br />
                  본인 외의 계정을 등록하면 <b>계정이 정지될 수 있습니다.</b><br />
                  <span style={{ color: '#B91C1C' }}>한 휴대폰에는 한 계정만 등록됩니다.</span>
                </div>
              </div>

              <p style={{ fontSize: 14, fontWeight: 700 }}>아이디</p>
              <input value={nickname} onChange={e => setNickname(e.target.value)}
                placeholder="가입한 아이디" style={input} />

              <p style={{ fontSize: 14, fontWeight: 700, marginTop: 14 }}>휴대폰번호</p>
              <input value={phone} onChange={e => setPhone(e.target.value)}
                inputMode="numeric" placeholder="010-1234-5678 (- 없이 입력해도 됩니다)" style={input} />

              <p style={{ fontSize: 14, fontWeight: 700, marginTop: 14 }}>비밀번호</p>
              <input value={pin} onChange={e => setPin(e.target.value)}
                type="password" placeholder="비밀번호" style={input} />

              <p style={{ fontSize: 14, fontWeight: 700, marginTop: 14 }}>비밀번호 확인</p>
              <input value={pin2} onChange={e => setPin2(e.target.value)}
                type="password" placeholder="비밀번호 다시 입력" style={input} />

              {msg && (
                <div style={{ marginTop: 14, padding: 12, borderRadius: 12, fontSize: 14, lineHeight: 1.6,
                              whiteSpace: 'pre-line',
                              background: msg.ok ? '#F0FDF4' : '#FEF2F2',
                              border: `1px solid ${msg.ok ? '#BBF7D0' : '#FECACA'}`,
                              color: msg.ok ? '#166534' : '#991B1B' }}>
                  {msg.text}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px 20px', borderTop: '1px solid #f3f4f6' }}>
          {done ? (
            <button onClick={onClose}
              style={{ width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 16,
                       fontWeight: 700, background: '#111827', color: '#fff', border: 0 }}>
              확인
            </button>
          ) : (
            <button onClick={submit} disabled={busy}
              style={{ width: '100%', padding: '15px 0', borderRadius: 14, fontSize: 17,
                       fontWeight: 800, background: '#FF6B00', color: '#fff', border: 0,
                       opacity: busy ? .5 : 1 }}>
              {busy ? '확인 중…' : '본인 확인하고 지문 등록'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
